import * as React from 'react';
import {
  blockReportProvider,
  IBlockReportState,
} from '../../../data/providers/reports/blockReportProvider';
import { formatTimeframe } from '../../../data/providers/utils/formatTimeframe';
import { Entity, EntityType } from '../../../data/types';
import { formatDayFromUtc } from '../../../utils/date';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../utils/ErrorMessages';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { ResultList } from './ResultList';
import { BlockSettings } from './settings/Settings';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const BlockReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<IBlockReportState>();
  const [rawValsView, setRawValsView] = React.useState(false);
  /**
   * @description subscribe to data provider
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = blockReportProvider(
      props.entityType,
      props.entity
    ).subscribe((state: IBlockReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename(
    'block',
    props.entity,
    formatTimeframe(
      reportState?.blockTimeframe?.from?.value,
      reportState?.blockTimeframe?.to?.value
    )
  );

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={(): void => null}
              filename={filename}
              title='Block Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
              landscape
            >
              <Column>
                <ReportOverview
                  noComparison
                  targetEntity={props.entity}
                  reportName={`Block Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <ResultList
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                  rawValsView={rawValsView}
                  onRawValsViewChange={setRawValsView}
                />
              </Column>
            </Pdf>
          </PdfContainer>
        ) : (
          <React.Fragment>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  noComparison
                  targetEntity={props.entity}
                  reportName={`Block Report - ${reportState?.settings?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <h5 style={{ margin: 'auto' }}>
                  {`${formatDayFromUtc(
                    reportState.blockTimeframe.from.value
                  )} - ${formatDayFromUtc(
                    reportState.blockTimeframe.to.value
                  )}`}
                </h5>
                <ResultList
                  reportState={reportState}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                  rawValsView={rawValsView}
                  onRawValsViewChange={setRawValsView}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </React.Fragment>
        )
      ) : props.loading ? (
        <LinearLoading />
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <BlockSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );

    case 'missing-baseline-date':
      return (
        <ErrorMessage iconName='date_range' text='Missing Date for Baseline'>
          <div style={{ marginTop: '10px' }}>
            Please select a date for the baseline.
          </div>
          <BlockSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );
    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            There are not enough data points to produce this report. Try
            changing the timeframe in settings
          </div>
          <BlockSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
